import { ReportSelectorControls } from 'src/components/star-grid/hooks/useReportSelectors';
import { MultiselectProps } from '@amzn/awsui-components-core/multiselect';
import { SelectProps } from '@amzn/awsui-components-core/select';
import { useMemo } from 'react';
import { PNL_STATEMENT_LINE_DIMENSION } from 'src/components/star-grid/starGridConstants';
import { PnlReportConfig } from 'src/components/star-grid/starGridTypes';
import { DataSyncText, useDataSyncText } from 'src/components/star-grid/hooks/useDataSyncText';
import { buildReportFilterFields, ReportFilterField, updateReportFilter } from 'src/components/star-grid/utils/reportFilters';
import { PNL_DEFAULT_SCALE, PNL_SCALE_OPTIONS } from 'src/components/star-grid/utils/pnlGrid';

export interface ReportFilterBarProps {
  readonly selectors?: ReportSelectorControls;
  readonly definition?: PnlReportConfig;
  readonly filters: Readonly<Record<string, readonly string[]>>;
  readonly dimensionLabels: Readonly<Record<string, string>>;
  readonly scaleId: string;
  readonly disabled: boolean;
  readonly onFiltersChange: (filters: Record<string, readonly string[]>) => void;
  readonly onScaleChange: (scale: string) => void;
  readonly onReset: () => void;
}

const filterControl = (field: ReportFilterField, props: ReportFilterBarProps, text: DataSyncText): MultiselectProps => ({
  selectedOptions: field.selectedOptions,
  options: field.options,
  onChange: ({ detail }) => props.onFiltersChange(updateReportFilter(props.filters, field.key, detail.selectedOptions, field.options.length)),
  placeholder: field.isAllSelected ? text('allValues') : field.selectedOptions.map((option) => option.label).join(', '),
  controlId: `report-filter-${field.key}`,
  ariaLabel: field.label,
  filteringType: 'auto',
  filteringPlaceholder: text('findValue'),
  hideTokens: true,
  empty: text('noFilterValues'),
  noMatch: text('noMatchingValues'),
  enableSelectAll: true,
  i18nStrings: { selectAllText: text('selectAllValues') },
  disabled: props.disabled || field.options.length === 0,
  expandToViewport: true
});

const useFilterFields = (props: ReportFilterBarProps, text: DataSyncText) =>
  useMemo(
    () =>
      buildReportFilterFields(props.definition, props.filters, {
        [PNL_STATEMENT_LINE_DIMENSION]: text('statementLine'),
        ...props.dimensionLabels
      }).map((field) => ({
        ...field,
        control: filterControl(field, props, text)
      })),
    [props.definition, props.filters, props.dimensionLabels, props.onFiltersChange, props.disabled, text]
  );

export const useReportFilterBar = (props: ReportFilterBarProps) => {
  const text = useDataSyncText();
  const fields = useFilterFields(props, text);
  const scaleOptions = PNL_SCALE_OPTIONS.map((scale) => ({ value: scale.id, label: text(scale.labelKey) }));
  const selectedScale =
    scaleOptions.find((scale) => scale.value === props.scaleId) ?? scaleOptions.find((scale) => scale.value === PNL_DEFAULT_SCALE.id)!;
  const changeScale = ({ detail }: { detail: SelectProps.ChangeDetail }): void =>
    props.onScaleChange(detail.selectedOption.value ?? PNL_DEFAULT_SCALE.id);
  return { text, fields, scaleOptions, selectedScale, changeScale };
};
